<script setup lang="ts">
import { Head, router } from '@inertiajs/vue3';
import { Loader2, Play } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import { store as runStore } from '@/actions/App/Http/Controllers/RunController';
import ActivityTracker from '@/components/activity/ActivityTracker.vue';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { getTypeLabel } from '@/composables/useActivityLabels';
import { useToast } from '@/composables/useToast';
import AppLayout from '@/layouts/AppLayout.vue';
import type { RunData } from '@/types/models';

interface Props {
    project: { id: string; name: string };
    runs: {
        data: RunData[];
        total: number;
        current_page: number;
        last_page: number;
    };
}

const props = defineProps<Props>();

defineOptions({
    layout: (props: { project: { id: string; name: string } }) => [
        AppLayout,
        {
            breadcrumbs: [
                { title: 'Projets', href: '/projects' },
                {
                    title: props.project.name,
                    href: `/projects/${props.project.id}`,
                },
                { title: 'Activité', href: '#' },
            ],
        },
    ],
});

const activityTypes = [
    'screening',
    'extraction',
    'search_equation',
    'pdf_matching',
    'zotero_import',
];

const STATUS_CHIPS = [
    { label: 'Tous', value: '' },
    { label: 'En cours', value: 'running' },
    { label: 'Terminés', value: 'completed' },
    { label: 'Échecs', value: 'failed' },
] as const;

const toast = useToast();
const selectedType = ref<string>('screening');
const statusFilter = ref<string>('');
const starting = ref(false);

const filteredRuns = computed(() => {
    if (!statusFilter.value) {
        return props.runs.data;
    }

    return props.runs.data.filter((r) => r.status === statusFilter.value);
});

function startActivity() {
    if (starting.value) {
        return;
    }

    starting.value = true;

    router.post(
        runStore().url,
        { project_id: props.project.id, type: selectedType.value },
        {
            preserveScroll: true,
            onSuccess: () => router.reload({ only: ['runs'] }),
            onError: (errors) => {
                const message = (errors as Record<string, string>).message;

                if (
                    message?.includes('already') ||
                    message?.includes('actif')
                ) {
                    toast.error(
                        'Un traitement est déjà en cours pour ce projet.',
                    );
                } else {
                    toast.error(message ?? "Impossible de lancer l'activité.");
                }
            },
            onFinish: () => {
                starting.value = false;
            },
        },
    );
}
</script>

<template>
    <Head :title="`Activité — ${project.name}`" />

    <div class="flex h-full flex-col overflow-y-auto">
        <div class="mx-auto w-full max-w-5xl px-6 py-6">
            <!-- Page header -->
            <div class="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div
                        class="text-xs font-semibold tracking-wider text-muted-foreground uppercase"
                    >
                        Suivi
                    </div>
                    <h1 class="mt-1 text-2xl font-semibold tracking-tight">
                        Activité du projet
                    </h1>
                    <p class="mt-1 text-sm text-muted-foreground">
                        Suivez les traitements lancés par l'assistant sur ce
                        projet.
                    </p>
                </div>

                <div class="flex items-center gap-2">
                    <!-- Filter chips -->
                    <div
                        class="flex gap-1 rounded-lg border border-border bg-muted/50 p-1"
                    >
                        <button
                            v-for="chip in STATUS_CHIPS"
                            :key="chip.value"
                            class="rounded-md px-3 py-1 text-sm font-medium transition-colors"
                            :class="
                                statusFilter === chip.value
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            "
                            @click="statusFilter = chip.value"
                        >
                            {{ chip.label }}
                        </button>
                    </div>

                    <Select v-model="selectedType">
                        <SelectTrigger class="w-48">
                            <SelectValue placeholder="Type d'activité" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem
                                v-for="type in activityTypes"
                                :key="type"
                                :value="type"
                            >
                                {{ getTypeLabel(type) }}
                            </SelectItem>
                        </SelectContent>
                    </Select>

                    <Button :disabled="starting" @click="startActivity">
                        <Loader2
                            v-if="starting"
                            class="mr-2 h-4 w-4 animate-spin"
                        />
                        <Play v-else class="mr-2 h-4 w-4" />
                        Lancer une activité
                    </Button>
                </div>
            </div>

            <!-- Activity list -->
            <div class="space-y-3">
                <p
                    v-if="filteredRuns.length === 0"
                    class="rounded-lg border border-dashed p-12 text-center text-muted-foreground"
                >
                    Aucune activité{{
                        statusFilter ? ' pour ce filtre' : ' pour ce projet'
                    }}.
                </p>

                <ActivityTracker
                    v-for="activity in filteredRuns"
                    :key="activity.id"
                    :activity="activity"
                />
            </div>

            <!-- Pagination -->
            <div
                v-if="runs.last_page > 1"
                class="mt-6 flex justify-center gap-2"
            >
                <Button
                    variant="outline"
                    size="sm"
                    :disabled="runs.current_page <= 1"
                    @click="
                        router.get(
                            `${$page.url.split('?')[0]}`,
                            { page: runs.current_page - 1 },
                            { preserveState: true, replace: true },
                        )
                    "
                >
                    Précédent
                </Button>
                <span class="flex items-center text-sm text-muted-foreground">
                    Page {{ runs.current_page }} / {{ runs.last_page }}
                </span>
                <Button
                    variant="outline"
                    size="sm"
                    :disabled="runs.current_page >= runs.last_page"
                    @click="
                        router.get(
                            `${$page.url.split('?')[0]}`,
                            { page: runs.current_page + 1 },
                            { preserveState: true, replace: true },
                        )
                    "
                >
                    Suivant
                </Button>
            </div>
        </div>
    </div>
</template>
